/**
 * Lot Filtering Operations
 *
 * Handles filtering lots based on various criteria (deleted, favorites, search text)
 * @module portal/modules/lotOperations/filtering
 */

import { state } from '../state';
import { Lot } from '../types';
import { hasPricingData } from '../rendering';
import { calculateMeatOnBone } from './financials';
import { getEffectiveCloseTime } from './status';

/**
 * Apply lot filters based on current state settings
 */
export function applyLotFilters(lots: Lot[]): Lot[] {
  if (!Array.isArray(lots)) return [];

  let filtered = lots;

  // Filter out deleted lots unless showDeleted is enabled
  if (!state.showDeleted) {
    filtered = filtered.filter(lot => !lot.deleted);
  }

  // Filter to only favorites if favorites chip active
  if (state.filter && state.filter.favorite === 'only') {
    filtered = filtered.filter(lot => lot.favorite);
  }

  // Apply search filter if search text exists
  if (state.q && state.q.trim()) {
    const searchLower = state.q.trim().toLowerCase();
    filtered = filtered.filter(lot => {
      const title = (lot.title || '').toLowerCase();
      const auctionTitle = (lot.auctionTitle || '').toLowerCase();
      const lotNumber = (lot.lotNumber || '').toLowerCase();
      const itemNumber = (lot.itemNumber || '').toLowerCase();
      const location = (lot.location || '').toLowerCase();
      const notes = (lot.notes || '').toLowerCase();

      return (
        title.includes(searchLower) ||
        auctionTitle.includes(searchLower) ||
        lotNumber.includes(searchLower) ||
        itemNumber.includes(searchLower) ||
        location.includes(searchLower) ||
        notes.includes(searchLower)
      );
    });
  }

  // Advanced filters
  if (state.filter) {
    // Closing today / this week
    if (state.filter.closing === 'today' || state.filter.closing === 'week') {
      const now = Date.now();
      const msInDay = 24 * 60 * 60 * 1000;
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = todayStart.getTime() + msInDay - 1;
      const weekEnd = todayStart.getTime() + 7 * msInDay - 1;
      filtered = filtered.filter(lot => {
        const closeTs = getEffectiveCloseTime(lot);
        if (!closeTs || closeTs <= 0) return false;
        if (closeTs < now) return false; // already closed
        if (state.filter!.closing === 'today') {
          return closeTs >= todayStart.getTime() && closeTs <= todayEnd;
        }
        // week
        return closeTs >= todayStart.getTime() && closeTs <= weekEnd;
      });
    }
    // High margin (≥30%) based on meatHigh/spendAmount
    if (state.filter.marginHigh === 'high') {
      filtered = filtered.filter(lot => {
        const meat = calculateMeatOnBone(lot as any, state.settings as any);
        if (!meat || meat.meatHigh == null || meat.spendAmount <= 0) return false;
        const pct = (meat.meatHigh / meat.spendAmount) * 100;
        return pct >= 30;
      });
    }
    // Needs price check (no pricing data and not pending AI)
    if (state.filter.needsPrice === 'needs') {
      filtered = filtered.filter(lot => {
        const hasPricing = hasPricingData(lot as any);
        const aiPending = lot.aiStatus === 'processing' || lot.aiStatus === 'pending';
        return !hasPricing && !aiPending && !lot.deleted;
      });
    }
    // Active bids (requires lot.bidding flag)
    if (state.filter.bidding === 'active') {
      filtered = filtered.filter(lot => (lot as any).bidding === true);
    }
  }

  return filtered;
}
